import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

const STORAGE_KEY = 'user_timezone';
export const DEFAULT_TIMEZONE = 'America/Mexico_City';

/**
 * Servicio de zona horaria del usuario.
 *
 * Política del proyecto:
 *   - BD almacena UTC; backend entrega ISO con offset (Z para Instant).
 *   - Este service decide la zona en la que se MUESTRA al usuario.
 *   - Default: America/Mexico_City (LATAM/MX).
 *   - Cada usuario puede cambiar su zona desde su perfil → PATCH al backend
 *     y sincroniza el cache local.
 *
 * Uso típico: el `AppDatePipe` lo inyecta y formatea con la zona actual.
 */
@Injectable({ providedIn: 'root' })
export class TimezoneService {

  private zone$ = new BehaviorSubject<string>(DEFAULT_TIMEZONE);

  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.zone$.next(this.readInitialZone());
  }

  /** Zona actual sincrónica (para pipes y getters). */
  getCurrentZone(): string {
    return this.zone$.getValue();
  }

  /** Zona actual reactiva — útil si el componente quiere reaccionar a cambios. */
  zoneChanges(): Observable<string> {
    return this.zone$.asObservable();
  }

  /**
   * Cambia la zona localmente y la persiste en localStorage. Si {@code persistRemote}
   * es true, hace PATCH al backend (no se usa al inicio del login).
   */
  setZone(zone: string, persistRemote: boolean = false, rol?: 'candidato' | 'empresa'): Observable<any> {
    if (!this.isValidIana(zone)) {
      return of({ error: 'Zona inválida' });
    }
    this.zone$.next(zone);
    if (isPlatformBrowser(this.platformId)) {
      try { localStorage.setItem(STORAGE_KEY, zone); } catch {}
    }
    if (!persistRemote || !rol) return of({ ok: true });
    const url = rol === 'candidato'
      ? `${environment.baseApi}/privado/candidato/perfil/timezone`
      : `${environment.baseApi}/privado/empresa/perfil/timezone`;
    return this.http.patch(url, { timezone: zone }).pipe(
      catchError(err => {
        console.warn('[TimezoneService] PATCH falló:', err?.status);
        return of({ ok: false });
      })
    );
  }

  /**
   * Sincroniza la zona desde el backend al login (o al refrescar). Es seguro
   * llamar repetidamente: usa cache local mientras el servidor responde.
   */
  syncFromServer(): Observable<string> {
    return this.http.get<any>(`${environment.baseApi}/privado/me/timezone`).pipe(
      tap(resp => {
        const tz = resp?.data?.timezone;
        if (tz && this.isValidIana(tz)) {
          this.zone$.next(tz);
          if (isPlatformBrowser(this.platformId)) {
            try { localStorage.setItem(STORAGE_KEY, tz); } catch {}
          }
        }
      }),
      catchError(err => {
        // 404 → admin u otro rol sin TZ persistida, mantenemos cache local.
        // Cualquier otra cosa la logueamos para depurar.
        if (err?.status !== 404) {
          console.warn('[TimezoneService] /me/timezone falló:', err?.status, err?.error?.message ?? err?.message);
        }
        return of(this.zone$.getValue());
      })
    ) as Observable<string>;
  }

  /** Útil para resetear al logout. */
  reset(): void {
    this.zone$.next(DEFAULT_TIMEZONE);
    if (isPlatformBrowser(this.platformId)) {
      try { localStorage.removeItem(STORAGE_KEY); } catch {}
    }
  }

  /** Verifica si una zona es IANA válida usando Intl.DateTimeFormat. */
  isValidIana(zone: string): boolean {
    if (!zone) return false;
    try {
      Intl.DateTimeFormat(undefined, { timeZone: zone });
      return true;
    } catch {
      return false;
    }
  }

  private readInitialZone(): string {
    if (!isPlatformBrowser(this.platformId)) return DEFAULT_TIMEZONE;
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && this.isValidIana(stored)) return stored;
    } catch {}
    return DEFAULT_TIMEZONE;
  }
}
